// Usage page (ADR-179 P2 / D5-P2). ListMyUsageEvents — account forced
// server-side (no account selector, unlike the operator's usage.tsx, which
// must pick one of many accounts). Same honest-gap note as the operator
// page about license_id/reporting_source/deployment_type reading back
// empty on every event today (server-side population gap, not specific to
// the customer path).
//
// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto the shared genesis.ts tokens + glass-card idiom.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as pb from '@/generated/console_pb'
import { listMyUsageEvents } from '@/lib/auth/customer-api'
import { dataClassificationLabel, formatTimestamp } from '@/lib/formatters'
import { DARK, GREY_300, GREY_400, GREY_500, WHITE, errorBlockStyle } from '@/lib/genesis'

const PAGE_SIZE = 50

export function PortalUsagePage() {
  const [instanceId, setInstanceId] = useState('')
  const [eventType, setEventType] = useState('')
  const [pageToken, setPageToken] = useState('')
  const [pageStack, setPageStack] = useState<string[]>([])

  const {
    data: usageData,
    isLoading: usageLoading,
    isError: usageError,
  } = useQuery({
    queryKey: ['portal-usage-events', instanceId, eventType, pageToken],
    queryFn: () =>
      listMyUsageEvents({
        instanceId: instanceId || undefined,
        eventType: eventType || undefined,
        pageSize: PAGE_SIZE,
        pageToken: pageToken || undefined,
      }),
  })

  const events = usageData?.getEventsList() ?? []

  const summary = useMemo(() => {
    const counts = new Map<string, number>()
    for (const ev of events) {
      const key = ev.getEventType() || '(unset)'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1])
  }, [events])

  function goNext() {
    const next = usageData?.getNextPageToken()
    if (!next) return
    setPageStack((s) => [...s, pageToken])
    setPageToken(next)
  }

  function goPrev() {
    setPageStack((s) => {
      if (s.length === 0) return s
      const copy = [...s]
      const prev = copy.pop() as string
      setPageToken(prev)
      return copy
    })
  }

  function resetPaging() {
    setPageToken('')
    setPageStack([])
  }

  return (
    <div>
      <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Usage</h1>

      <div className="glass-card mb-4 flex flex-wrap items-end gap-3 p-4">
        <div>
          <label htmlFor="portal-usage-instance" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
            Instance ID (optional)
          </label>
          <input
            id="portal-usage-instance"
            value={instanceId}
            onChange={(e) => {
              setInstanceId(e.target.value)
              resetPaging()
            }}
            placeholder="filter by instance_id"
            className="glass-input w-56 px-2 py-1.5 text-sm"
            style={{ color: WHITE }}
          />
        </div>
        <div>
          <label htmlFor="portal-usage-event-type" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
            Event type (optional)
          </label>
          <input
            id="portal-usage-event-type"
            value={eventType}
            onChange={(e) => {
              setEventType(e.target.value)
              resetPaging()
            }}
            placeholder="filter by event_type"
            className="glass-input w-56 px-2 py-1.5 text-sm"
            style={{ color: WHITE }}
          />
        </div>
      </div>

      {usageLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading usage events…</p>}
      {usageError && (
        <div className="p-3" style={errorBlockStyle}>
          <p className="text-sm" style={{ color: WHITE }}>Failed to load usage events.</p>
        </div>
      )}

      {!usageLoading && !usageError && (
        <>
          {summary.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {summary.map(([type, count]) => (
                <span key={type} className="rounded-full px-2 py-1 text-xs" style={{ backgroundColor: DARK, color: GREY_300 }}>
                  {type}: <span className="font-mono" style={{ color: WHITE }}>{count}</span>
                </span>
              ))}
            </div>
          )}

          {events.length === 0 && (
            <p className="text-sm" style={{ color: GREY_400 }}>No usage events for this page of filters.</p>
          )}

          {events.length > 0 && (
            <div className="glass-card overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead>
                  <tr className="text-left" style={{ borderBottom: `1px solid ${GREY_500}`, color: GREY_400 }}>
                    <th className="px-3 py-2 font-medium">Occurred at</th>
                    <th className="px-3 py-2 font-medium">Event type</th>
                    <th className="px-3 py-2 font-medium">Instance</th>
                    <th className="px-3 py-2 font-medium">Active seats</th>
                    <th className="px-3 py-2 font-medium">Classification</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev: pb.UsageEvent) => (
                    <tr key={ev.getId()} className="transition-colors hover:bg-white/5" style={{ borderBottom: `1px solid ${DARK}` }}>
                      <td className="px-3 py-2 font-mono" style={{ color: GREY_300 }}>{formatTimestamp(ev.getOccurredAt())}</td>
                      <td className="px-3 py-2" style={{ color: WHITE }}>{ev.getEventType() || '—'}</td>
                      <td className="px-3 py-2" style={{ color: GREY_400 }}>{ev.getInstanceId() || '—'}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: GREY_400 }}>{ev.getActiveSeatCount() || '—'}</td>
                      <td className="px-3 py-2" style={{ color: GREY_400 }}>
                        {dataClassificationLabel(ev.getDataClassification())}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={pageStack.length === 0}
              onClick={goPrev}
              className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
              style={{ color: GREY_300 }}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!usageData?.getNextPageToken()}
              onClick={goNext}
              className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
              style={{ color: GREY_300 }}
            >
              Next
            </button>
          </div>

          <p className="mt-4 max-w-2xl text-xs" style={{ color: GREY_500 }}>
            Note: <code className="rounded px-1 py-0.5 font-mono" style={{ backgroundColor: DARK }}>license_id</code>,{' '}
            <code className="rounded px-1 py-0.5 font-mono" style={{ backgroundColor: DARK }}>reporting_source</code>, and{' '}
            <code className="rounded px-1 py-0.5 font-mono" style={{ backgroundColor: DARK }}>deployment_type</code> read back
            empty/unspecified on every event as of this build — a documented server-side gap, not
            specific to this portal. Those columns are intentionally not rendered above.
          </p>
        </>
      )}
    </div>
  )
}
